import {
	createContext,
	forwardRef,
	useContext,
	useId,
	useMemo,
	useState,
} from "react"
import { Box, BoxOwnProps } from "./Box"
import { useDocumentFileDrag, useFileDrop } from "./react-hooks"
import { btnHeightClassName } from "./button.css"
import { Inline } from "./Inline"
import { Text } from "./Text"
import {
	textInputClassName,
	selectClassName,
	fileInputClassName,
	fileDropTargetClassName,
} from "./input.css"
import classNames from "classnames"
import { duration, BaseUnitOfTime } from "@sembark-travel/datetime-utils"
import Icons from "./Icons"
import { Stack } from "./Stack"
export { noSpinnersClassName } from "./input.css"

const InputGroupContext = createContext<{
	hasError?: boolean
}>({
	hasError: false,
})

function useInputGroupContext() {
	return useContext(InputGroupContext)
}

export function InputGroup({
	hasError,
	children,
}: {
	hasError?: boolean
	children?: React.ReactNode
}) {
	const context = useMemo(() => {
		return { hasError: hasError || false }
	}, [hasError])
	return (
		<InputGroupContext.Provider value={context}>
			{children}
		</InputGroupContext.Provider>
	)
}

export type BaseInputProps = Omit<BoxOwnProps, "size"> & {
	hasError?: boolean
}

const baseInputProps: Omit<BoxOwnProps, "size"> = {
	bgColor: "default",
	borderWidth: "1",
	rounded: "md",
	borderColor: { default: "emphasis", focus: "primary_emphasis" },
	paddingY: "2",
	paddingLeft: "4",
	paddingRight: "4",
	maxWidth: "full",
	display: "block",
}

export type InputProps = BaseInputProps & {
	size?: keyof typeof btnHeightClassName
} & Omit<React.HTMLProps<HTMLInputElement>, keyof BoxOwnProps | "as">

export const Input = forwardRef<HTMLInputElement, InputProps>(
	function InputForwarded(
		{ type, hasError, className, size = "base", disabled, ...props },
		ref
	) {
		const context = useInputGroupContext()
		hasError = hasError || context.hasError
		return (
			<Box<"input">
				{...baseInputProps}
				disabled={disabled}
				width={
					type === "email" ||
					type === "text" ||
					type === "password" ||
					type === "search"
						? "full"
						: undefined
				}
				paddingRight={
					type === "number"
						? "1"
						: size === "sm"
							? "2"
							: baseInputProps.paddingRight
				}
				paddingLeft={size === "sm" ? "2" : baseInputProps.paddingRight}
				borderColor={
					hasError
						? "danger_emphasis"
						: {
								default: "emphasis",
								focus: "primary_emphasis",
							}
				}
				id={props.id || `field_${props.name || "input"}`}
				data-testid={props.id || `field_${props.name || "input"}`}
				{...props}
				backgroundColor={disabled ? "subtle" : props.backgroundColor}
				opacity={disabled ? "70" : props.opacity}
				className={classNames(
					btnHeightClassName[size],
					textInputClassName,
					className
				)}
				appearance="none"
				as="input"
				ref={ref}
				type={type}
				{...(type === "number" && {
					onWheel: (e) => {
						e.currentTarget.blur()
					},
				})}
			/>
		)
	}
)
Input.displayName = "Input"

type RadioInputProps = Omit<InputProps, "size" | "type">
export const RadioInput = forwardRef<HTMLInputElement, RadioInputProps>(
	function RadioInputForwarded({ hasError, ...props }, ref) {
		return (
			<Box<"input">
				{...baseInputProps}
				display="inlineBlock"
				paddingX="0"
				paddingY="0"
				rounded="full"
				verticalAlign="middle"
				id={props.id || `field_${props.name || "input"}`}
				data-testid={props.id || `field_${props.name || "input"}`}
				{...props}
				as="input"
				ref={ref}
				type="radio"
				cursor={props.disabled ? "disabled" : undefined}
			/>
		)
	}
)
RadioInput.displayName = "RadioInput"

type CheckboxInputProps = Omit<InputProps, "size" | "type">
export const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
	function CheckboxInputForwarded({ hasError, ...props }, ref) {
		return (
			<Box<"input">
				{...baseInputProps}
				display="inlineBlock"
				paddingX="0"
				paddingY="0"
				rounded="full"
				verticalAlign="middle"
				id={props.id || `field_${props.name || "input"}`}
				data-testid={props.id || `field_${props.name || "input"}`}
				{...props}
				as="input"
				ref={ref}
				type="checkbox"
				cursor={props.disabled ? "disabled" : undefined}
			/>
		)
	}
)
CheckboxInput.displayName = "CheckboxInput"

type SwitchInputProps = Omit<CheckboxInputProps, "onChange"> & {
	onChange?: (checked: 0 | 1) => void
}
export const SwitchInput = forwardRef<HTMLInputElement, SwitchInputProps>(
	function SwitchInputForwarded(
		{ hasError, onChange, checked, ...props },
		ref
	) {
		return (
			<CheckboxInput
				{...props}
				ref={ref}
				checked={checked}
				onChange={() =>
					props.readOnly || props.disabled
						? undefined
						: onChange?.(checked ? 0 : 1)
				}
			/>
		)
	}
)
SwitchInput.displayName = "SwitchInput"

type SelectInputProps = BaseInputProps & {
	size?: keyof typeof btnHeightClassName
} & Omit<React.HTMLProps<HTMLSelectElement>, keyof BoxOwnProps | "as">
export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
	function SelectForwared(
		{ hasError, className, size = "base", ...props },
		ref
	) {
		const context = useInputGroupContext()
		hasError = hasError || context.hasError
		return (
			<Box<"select">
				{...baseInputProps}
				className={classNames(
					textInputClassName,
					selectClassName,
					btnHeightClassName[size]
				)}
				paddingY="0"
				borderColor={
					hasError
						? "danger_emphasis"
						: { default: "emphasis", focus: "primary_emphasis" }
				}
				id={props.id || `field_${props.name || "input"}`}
				data-testid={props.id || `field_${props.name || "input"}`}
				{...props}
				appearance="none"
				as="select"
				ref={ref}
			/>
		)
	}
)
SelectInput.displayName = "SelectInput"

type InlineSelectInputProps = BaseInputProps & {
	noCaret?: boolean
} & Omit<React.HTMLProps<HTMLSelectElement>, keyof BoxOwnProps | "as">
export const InlineSelectInput = forwardRef<
	HTMLSelectElement,
	InlineSelectInputProps
>(function SelectForwared(
	{ value, children, onChange, noCaret, disabled },
	ref
) {
	const id = `inline_select_input_` + useId()
	return (
		<Box position="relative" display="inlineBlock">
			<Box
				as="label"
				style={{ fontWeight: "inherit" }}
				htmlFor={id}
				cursor={disabled ? "disabled" : "pointer"}
			>
				{value || "--"}
				{noCaret ? null : (
					<>
						{" "}
						<Icons.ChevronDown />
					</>
				)}
			</Box>
			<Box
				position="absolute"
				overflow="hidden"
				inset="0"
				title="Select options"
			>
				<select
					value={value}
					id={id}
					ref={ref}
					style={{
						background: "transparent",
						opacity: 0,
						cursor: !disabled ? "pointer" : "not-allowed",
					}}
					onChange={onChange}
					disabled={disabled}
				>
					{children}
				</select>
			</Box>
		</Box>
	)
})
InlineSelectInput.displayName = "InlineSelectInput"

type TextAreaProps = BaseInputProps &
	Omit<React.HTMLProps<HTMLTextAreaElement>, keyof BoxOwnProps | "as">
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	function TextAreaForwared({ hasError, className, ...props }, ref) {
		const context = useInputGroupContext()
		hasError = hasError || context.hasError
		return (
			<Box<"textarea">
				{...baseInputProps}
				width="full"
				borderColor={
					hasError
						? "danger_emphasis"
						: { default: "emphasis", focus: "primary_emphasis" }
				}
				{...props}
				className={classNames(textInputClassName, className)}
				as="textarea"
				ref={ref}
			/>
		)
	}
)
TextArea.displayName = "TextArea"

function filesToShow(
	files: File | FileList | null | undefined,
	pickedFiles: FileList | null
): Array<File> {
	// `files` is the field's own value when the caller passes it, so a file
	// set from outside (a drop elsewhere, a reset) shows too.
	const shown = files === undefined ? pickedFiles : files
	if (!shown) return []
	return shown instanceof File ? [shown] : Array.from(shown)
}

export const FileInput = forwardRef<
	HTMLInputElement,
	Omit<InputProps, "type" | "onChange"> & {
		onChange?: (files: FileList | null) => void
		/**
		 * The field's current file(s), when its value is kept outside, e.g. in
		 * a form. What the input shows follows it.
		 */
		files?: File | FileList | null
	}
>(function InputForwarded(
	{
		hasError,
		id,
		size = "base",
		onFocus,
		onBlur,
		onChange,
		disabled,
		readOnly,
		accept,
		multiple,
		files,
	},
	ref
) {
	const context = useInputGroupContext()
	const [fieldValue, setFieldValue] = useState<string>("")
	const [pickedFiles, setPickedFiles] = useState<FileList | null>(null)
	const [dropError, setDropError] = useState<string | null>(null)
	const { isDragging, dropHandlers } = useFileDrop(
		(dropped, rejected) => {
			if (rejected.length) {
				setDropError("This file type is not accepted here.")
				return
			}
			if (!multiple && dropped.length > 1) {
				setDropError("Drop one file.")
				return
			}
			setDropError(null)
			// The picker keeps no path for a dropped file; cleared, it still
			// notices the same file picked again.
			setFieldValue("")
			setPickedFiles(dropped)
			onChange?.(dropped)
		},
		accept,
		disabled || readOnly
	)

	const isFileDragOnPage = useDocumentFileDrag(disabled || readOnly)

	hasError = hasError || context.hasError
	const outputLabelId = `${id || "file_input_"}_output`
	const shownFiles = filesToShow(files, pickedFiles)
	return (
		<Inline
			flexWrap="nowrap"
			pointerEvents={disabled ? "none" : undefined}
			alignItems="stretch"
			className={classNames(btnHeightClassName[size], {
				[fileDropTargetClassName]: isFileDragOnPage,
			})}
			{...baseInputProps}
			{...dropHandlers}
			paddingY="0"
			width="auto"
			overflow="hidden"
			paddingX="0"
			borderColor={
				hasError || dropError
					? "danger_emphasis"
					: isDragging
						? "primary_emphasis"
						: { default: "emphasis", focus: "primary_emphasis" }
			}
			maxWidth="xl"
		>
			<Inline
				as="label"
				position="relative"
				alignItems="center"
				justifyContent="center"
				htmlFor={id}
				borderRightWidth="1"
			>
				<Inline alignItems="center" paddingX="4" whiteSpace="preserve">
					Choose File{multiple ? "s" : ""}
				</Inline>
				<Box<"input">
					as="input"
					type="file"
					id={id}
					accept={accept}
					multiple={multiple}
					disabled={disabled}
					readOnly={readOnly}
					className={fileInputClassName}
					value={fieldValue}
					onChange={({ currentTarget }) => {
						let files = currentTarget.files
						if (files && !files.length) {
							// if no file selected, set the values as null
							files = null
						}
						const fieldValue = currentTarget.value
						onChange?.(files)
						setPickedFiles(files)
						setFieldValue(fieldValue)
						setDropError(null)
					}}
					// Cleared as the picker opens, so the same file picked again —
					// after a form reset, say — still counts as a change.
					onClick={() => setFieldValue("")}
					onFocus={onFocus}
					onBlur={onBlur}
					ref={ref}
					aria-describedby={outputLabelId}
				/>
			</Inline>
			<Inline
				aria-hidden="true"
				paddingX="4"
				alignItems="center"
				bgColor="subtle"
				flex="1"
				minWidth="0"
			>
				<Text
					maxWidth="full"
					fontSize="sm"
					textOverflow="truncate"
					id={outputLabelId}
					color={dropError ? "danger" : undefined}
				>
					{dropError
						? dropError
						: isDragging
							? `Drop ${multiple ? "files" : "a file"} here`
							: shownFiles.length > 1
								? `${shownFiles.length} files selected`
								: (shownFiles[0]?.name ?? "No file selected")}
				</Text>
			</Inline>
		</Inline>
	)
})
Input.displayName = "Input"

export function InputValidationErrorMessage(
	props: React.ComponentProps<typeof Box>
) {
	return (
		<Box
			backgroundColor="danger_emphasis"
			color="on_emphasis"
			display="block"
			rounded="md"
			fontSize="sm"
			paddingY="1"
			paddingX="2"
			role="alert"
			style={{
				width: "fit-content",
			}}
			{...props}
		/>
	)
}

type DurationInputProps = BaseInputProps & {
	size?: keyof typeof btnHeightClassName
	onChange: (value: string) => void
} & Omit<
		React.HTMLProps<HTMLInputElement>,
		keyof BoxOwnProps | "as" | "onChange"
	>
export const DurationInput = forwardRef<HTMLDivElement, DurationInputProps>(
	function DurationInputForwared(
		{ hasError, className, size = "base", value, onChange, ...props },
		ref
	) {
		const context = useInputGroupContext()
		hasError = hasError || context.hasError
		const dur = String(value).trim().length
			? duration(String(value))
			: undefined
		const [unit, setUnit] = useState<BaseUnitOfTime | undefined>(() => {
			if (dur?.isValid()) {
				const minutes = dur.asMinutes()
				if (minutes) {
					return "minutes"
				}
				const hours = dur.asHours()
				if (hours) {
					return "hours"
				}
				const days = dur.asDays()
				if (days) {
					return "days"
				}
				return "minutes"
			}
			return
		})
		function handleChange(
			d: ReturnType<typeof duration> | undefined = undefined
		) {
			onChange(d?.toISOString() || "")
		}
		return (
			<Inline ref={ref} gap="2">
				<Input
					{...baseInputProps}
					className={classNames(textInputClassName, btnHeightClassName[size])}
					paddingY="0"
					borderColor={
						hasError
							? "danger_emphasis"
							: { default: "emphasis", focus: "primary_emphasis" }
					}
					{...props}
					type="text"
					onChange={({ currentTarget: { value } }) => {
						if (String(value)) {
							if (!unit) {
								setUnit("minutes")
							}
							if (!dur?.isValid()) {
								handleChange(duration(value, "minutes"))
							} else {
								handleChange(duration(value, unit || "minutes"))
							}
						} else {
							handleChange()
						}
					}}
					value={dur?.isValid() ? dur.as(unit || "minutes") : ""}
					style={{ width: "80px" }}
					placeholder="30"
				/>
				<SelectInput
					{...baseInputProps}
					className={classNames(
						textInputClassName,
						selectClassName,
						btnHeightClassName[size]
					)}
					paddingY="0"
					borderColor={
						hasError
							? "danger_emphasis"
							: { default: "emphasis", focus: "primary_emphasis" }
					}
					{...(props as object)}
					appearance="none"
					placeholder="Min"
					onChange={({ currentTarget }) => {
						const value = currentTarget.value as BaseUnitOfTime
						const existingUnit = unit
						setUnit(value)
						if (!value) {
							handleChange()
							return
						}
						// change the unit, keeping the same amount
						handleChange(
							duration(dur?.as(existingUnit || "minutes") || 0, value)
						)
					}}
					value={unit}
				>
					<option></option>
					<option value="minutes">Mins</option>
					<option value="hours">Hrs</option>
					<option value="days">Days</option>
				</SelectInput>
			</Inline>
		)
	}
)
DurationInput.displayName = "DurationInput"

const DEFAULT_PRESET_COLORS = [
	"#ef4444",
	"#f97316",
	"#eab308",
	"#22c55e",
	"#06b6d4",
	"#3b82f6",
	"#8b5cf6",
	"#ec4899",
	"#64748b",
]
type ColorInputProps = InputProps & {
	presetColors?: Array<string>
}
export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(
	function ColorInputForwarded(
		{
			className,
			value,
			onChange,
			presetColors = DEFAULT_PRESET_COLORS,
			...props
		},
		ref
	) {
		const currentValue = String(value)

		function handleChange(color: string) {
			if (typeof onChange === "function") {
				onChange({
					target: {
						value: color,
					},
				} as React.ChangeEvent<HTMLInputElement>)
			}
		}

		return (
			<Stack gap="2">
				<Box position="relative">
					<Input
						{...props}
						ref={ref}
						type="text"
						value={currentValue}
						onChange={onChange}
						textTransform="uppercase"
						paddingLeft="12"
						paddingRight="12"
					/>

					<Box
						position="absolute"
						top="0"
						left="0"
						height="full"
						display="flex"
						alignItems="center"
						paddingLeft="4"
						pointerEvents="none"
					>
						<Box
							width="5"
							height="5"
							rounded="full"
							borderWidth="1"
							borderColor="emphasis"
							style={{
								backgroundColor: currentValue,
							}}
						/>
					</Box>

					<Box
						position="absolute"
						top="0"
						right="0"
						height="full"
						display="flex"
						alignItems="center"
						paddingRight="4"
					>
						<Box position="relative">
							<Box<"input">
								as="input"
								type="color"
								value={currentValue}
								onChange={(e) => {
									handleChange(e.currentTarget.value)
								}}
								style={{
									position: "absolute",
									inset: 0,
									opacity: 0,
									cursor: "pointer",
								}}
							/>

							<Box color="muted">
								<Icons.Pipette />
							</Box>
						</Box>
					</Box>
				</Box>

				{presetColors.length ? (
					<Inline gap="2" flexWrap="wrap">
						{presetColors.map((color) => (
							<Box
								key={color}
								width="5"
								height="5"
								cursor="pointer"
								borderWidth="1"
								borderColor="emphasis"
								rounded="full"
								style={{
									backgroundColor: color,
								}}
								onClick={() => handleChange(color)}
							/>
						))}
					</Inline>
				) : null}
			</Stack>
		)
	}
)

ColorInput.displayName = "ColorInput"
